import React, { useEffect, useRef, useState, useCallback } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { AVATAR_IMAGE_URL, GETMONTHLYLEADERBOARDLIST } from '../helpers/Constant';
import { postDataWithToken } from '../api/api';
import { getUserInfo } from '../utils/common';
import BackButton from '../common/backButton';

const LIMIT = 20;

function Leaderboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [monthlyLeaderboardData, setMonthlyLeaderboardData] = useState([]);
  const [monthlyUserPosition, setMonthlyUserPosition] = useState(0);
  const [daysUntilMonthEnd, setDaysUntilMonthEnd] = useState(0);
  const [userData, setUserData] = useState(null);

  const observer = useRef(null);
  const listRef = useRef(null);
  const fetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageRef = useRef(1);

  const calculateDaysUntilMonthEnd = () => {
    const now = moment();
    const endOfMonth = moment().endOf('month');
    return endOfMonth.diff(now, 'days');
  };

  function getPositionSuffix(position) {
    const suffixes = ['st', 'nd', 'rd'];
    const remainder = position % 10;

    if (position === 11 || position === 12 || position === 13) {
      return `${position}th`;
    }

    return `${position}${suffixes[remainder - 1] || 'th'}`;
  }

  const fetchMonthlyLeaderboard = useCallback(async (pageNo = 1) => {
    if (fetchingRef.current) return;
    if (pageNo > 1 && !hasMoreRef.current) return;

    fetchingRef.current = true;

    try {
      if (pageNo > 1) {
        setIsFetchingMore(true);
      } else {
        setLoading(true);
      }

      if (pageNo === 1) {
        const currentUser = getUserInfo();
        setUserData(currentUser);
        setDaysUntilMonthEnd(calculateDaysUntilMonthEnd());
      }

      const response = await postDataWithToken(GETMONTHLYLEADERBOARDLIST, {
        page: pageNo,
        limit: LIMIT,
      });

      
      const payload = response?.status === 200 ? (response.data || {}) : {};
      const users = Array.isArray(payload.users) ? payload.users : [];
      const currentPage = Number(payload.page) || pageNo;
      const totalPages = Number(payload.totalPages) || 0;
      const nextHasMore =
        typeof payload.hasMore === 'boolean'
          ? payload.hasMore
          : currentPage < totalPages;

      if (pageNo === 1) {
        setMonthlyLeaderboardData(users);
      } else {
        setMonthlyLeaderboardData((prev) => {
          const existingIds = new Set(
            prev.map((item) => (item.user?._id || item.user)?.toString())
          );
          const uniqueUsers = users.filter((item) => {
            const id = (item.user?._id || item.user)?.toString();
            return id && !existingIds.has(id);
          });
          return [...prev, ...uniqueUsers];
        });
      }

      setMonthlyUserPosition(Number(payload.currentUserRank) || 0);
      setHasMore(nextHasMore);
      hasMoreRef.current = nextHasMore;
      pageRef.current = currentPage;
      setPage(currentPage);
    } catch (err) {
      console.log(err);
      if (pageNo === 1) {
        setMonthlyLeaderboardData([]);
        setMonthlyUserPosition(0);
        setHasMore(false);
        hasMoreRef.current = false;
      }
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchMonthlyLeaderboard(1);
  }, [fetchMonthlyLeaderboard]);

  const lastUserRef = useCallback(
    (node) => {
      if (observer.current) {
        observer.current.disconnect();
      }

      if (!node || loading || isFetchingMore || !hasMore) return;

      observer.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            hasMoreRef.current &&
            !fetchingRef.current
          ) {
            fetchMonthlyLeaderboard(pageRef.current + 1);
          }
        },
        {
          root: listRef.current,
          rootMargin: '0px 0px 80px 0px',
          threshold: 0.1,
        }
      );

      observer.current.observe(node);
    },
    [loading, isFetchingMore, hasMore, fetchMonthlyLeaderboard]
  );

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return (
    <div className='wrapper h-100 d-flex flex-column p-0'>
      {loading ? (
        <div className='loader'>
          <Oval color="#daff00" secondaryColor="#ffffff" strokeWidth={3} strokeWidthSecondary={3} height={50} width={50} />
        </div>
      ) : null}
      
      <div className='reward_info_wrapper leaderboard-fullscreen flex-fill d-flex flex-column mt-0'>
        <div className='reward_chart p-3 bronze-chart d-flex flex-column overflow-hidden h-100'>
          <div className='row'>
            <div className='col-12 px-3'>
              <BackButton label={t('Home')} closeFunction={() => navigate(-1)} />
              <h2 className='text-white mb-3 text-center'>Monthly Leaderboard</h2>
              <h4 className='text-white mb-1 opacity-75'>
                <span className='me-1'>{t('Ends in')}</span>
                {daysUntilMonthEnd} {daysUntilMonthEnd === 1 ? t('day') : t('days')}
              </h4>
              <p className='mb-2 text-white opacity-50 lh-1'>
                {t('You are currently at')}{" " + getPositionSuffix(monthlyUserPosition) + " "}{t('place')}
              </p>
            </div>
          </div>

          <ul ref={listRef} className='reward_list pt-2 px-1 m-0 monthly-leaderboard-list'>
            {monthlyLeaderboardData && monthlyLeaderboardData.length > 0 ? (
              monthlyLeaderboardData.map((item, index) => {
                const userName = item.fname || item.lname ? `${item.fname + ' '}${item.lname}`.trim() : 'User';
                const itemUserId = (item.user?._id || item.user)?.toString();
                const isCurrentUser = itemUserId === userData?._id?.toString();
                const isLastItem = index === monthlyLeaderboardData.length - 1;

                return (
                  <li key={`${itemUserId || 'user'}-${item.rank || index}`} ref={isLastItem ? lastUserRef : null} className={`d-flex align-items-center justify-content-between py-1 px-2 ${isCurrentUser ? 'active' : ''}`}>
                    <div className='content_part d-flex align-items-center w-100 flex-nowrap'>
                      <div className='d-block reward_item_wrapper count rounded-circle'>
                        <span className='rounded-circle text-white'>{item.rank || index + 1}</span>
                      </div>
                      <div className='d-block reward_profile_wrapper'>
                        {item.avtar ? (
                          <img src={AVATAR_IMAGE_URL + itemUserId + "/" + item.avtar} alt='Leaderboard user' className='rounded-circle profile_img' />
                        ) : (
                          <div className='rounded-circle profile_img text-center d-flex align-items-center justify-content-center avtar_alternate border border-white'>
                            {userName.split(' ').map((word, idx) => (
                              <h6 key={idx} className='mb-0 text-white fw-bold text-uppercase'>
                                {word[0]}
                              </h6>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='user_info flex-fill'>
                        <h5 className='fw-bold mb-0 text-white'>{userName}</h5>
                        <p className={`mb-0 opacity-75 ${item.status === 'Online' ? 'yellow' : 'text-light'}`}>{t(item.status || 'Away')}</p>
                      </div>
                      <div className='d-block reward_price_wrapper'>
                        <div className='reward_price d-flex align-items-center ms-auto p-1 gap-1'>
                          <span className='fw-bold d-flex align-items-center justify-content-center rounded-circle point_icon'></span>
                          <h4 className='my-0 fw-bold text-white m-auto'>{item.point || 0}</h4>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })
            ) : (
              !loading && (
                <li className='d-flex align-items-center justify-content-center py-3'>
                  <p className='mb-0 text-white opacity-50'>{t('No leaderboard data available')}</p>
                </li>
              )
            )}
          </ul>

          {isFetchingMore && (
            <div className='d-flex justify-content-center align-items-center py-3 w-100'>
              <Oval height={30} width={30} color="#daff00" secondaryColor="#ffffff" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
